import { useState, useRef } from 'react';
import { Form, Alert } from 'react-bootstrap';
import { FloatingLabel } from './FloatingLabel';
import PropTypes from 'prop-types';
import Button from './Button';
import useNetwork from '../hooks/networkHook';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { Validator } from './ValidationResult';
import DefaultButton from './DefaultButton';
import { materializeValue } from '../utils/lazyValue';

export const customCtrlDef = {
    toString: ({ value, defaultValue }) => {
        if (!value) {
            return defaultValue;
        }
        return '<file>';
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, href, ...props }) => <FileInput href={href ?? def.href} {...props}></FileInput>
}

/**
 * A simple control to allow a file to be uploaded and downloaded with it being stored as a base64 encoded data url.
 * @component
 */
const FileInput = ({ placeholder, value, disabled = false, required = false, hideLabel = false, onChange, href }) => {
    const sizeLimit = 10;
    const { t } = useTranslation();
    const [error, setError] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const network = useNetwork();
    const [validity, setValidity] = useState(null);

    const processFile = (file) => {
        if(file?.size > 0){
            if (file && file.size <= sizeLimit * 1024 * 1024) {
                const reader = new FileReader();
                reader.onload = (readEvent) => {
                    onChange({ name: file.name, data: readEvent.target.result });
                    setError(false);
                };
                reader.readAsDataURL(file);
            } else {
                setError(t('fileErrorSize', { maxSize: sizeLimit + 'MB'}));
            }
    }}

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const valid = fileInputRef.current.checkValidity();
        processFile(file);
        setValidity(valid);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
        if (event.dataTransfer.items?.length === 1 && event.dataTransfer.items[0].kind === 'file') {
            const file = event.dataTransfer.files[0];
            processFile(file);
        } else {
            setError(t('fileErrorSingle'));
        }
    };

    const handleClick = () => {
        if (!value){
            fileInputRef.current.click();
        }
    };

    const handleDragOver = (event) => {
        if (event.dataTransfer.items?.length === 1 && event.dataTransfer.items[0].kind === 'file') {
            event.preventDefault();
            event.stopPropagation();
            setIsDragOver(true);
        }
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const doDownload = async e => {
        e.preventDefault();
        e.stopPropagation();
        const data = await materializeValue(value?.data);
        if (!data) {
            return;
        }
        network.downloadBlob(network.dataURIToBlob(data), value.name);
    }

    const style = {
        ...(value ? {
            border: isDragOver ? '3px solid blue' : null
        } : {
            border: isDragOver ? '3px solid blue' : '3px dashed lightgrey',
            color: 'lightgrey',
            textAlign: 'center',
            cursor: 'pointer'
        }), ...({ cursor: disabled ? null : 'cursor' })
    };

    
    const isInvalid = (validity === false) || error;

    if (disabled) {
        return <FloatingLabel placeholder={placeholder} value={value} hideLabel={hideLabel} href={href}>
            { value && <Button variant="link" onClick={e => doDownload(e)}><Icon intent="download" style={style}></Icon> {value.name ?? t('download')}</Button> }
        </FloatingLabel>
    }

    return (
        <FloatingLabel placeholder={placeholder} value={true} hideLabel={hideLabel} href={href}>
            <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={handleClick} style={style} className="d-flex align-items-center">
                {validity === false && <Alert variant="danger">{t('fileRequired')}</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}
                {value ? <Button variant="link" onClick={e => doDownload(e)} className="user-select-none"><Icon intent="download" style={style}></Icon> {value.name}</Button> : t('fileUploadPrompt')}
                <Validator value={value} ref={fileInputRef} onValidation={setValidity} nonblockingError={error}>
                    <Form.File
                        isInvalid={isInvalid}
                        label={t('fileUploadPrompt')}
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        required={!value && required}
                    />
                </Validator>
                {value && <div className="d-flex flex-row">
                    <DefaultButton kind="upload" variant="outline-dark" hideLabel={true} onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }} size="sm" className="mr-2 w-100"></DefaultButton>
                    <DefaultButton kind="delete" variant="outline-danger" hideLabel={true} onClick={() => { if (fileInputRef.current) { fileInputRef.current.value = null; onChange(null); } }} size="sm" className="w-100" confirmMessage={t('confirmDeleteItem', { item: value?.name })} />
                </div>}
            </div>
        </FloatingLabel>
    );
};

FileInput.propTypes = {
    /**
     * The text to be displayed by the control
     */
    value: PropTypes.string,

    /**
     * Function to call when the list of selection values has been changed
     */
    onChange: PropTypes.func.isRequired,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string.isRequired,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default FileInput;
